import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isPlatformAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const adminCheckDone = useRef(false);

  // Phase 1: Auth state — synchronous callback only, no Supabase calls.
  // Calling supabase.from() or supabase.auth.getUser() inside onAuthStateChange
  // causes a re-entrant lock deadlock in @supabase/auth-js's _acquireLock.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        // TOKEN_REFRESHED fires on tab focus with a fresh user object of the same
        // id. Preserving the previous reference prevents the admin-check effect
        // from re-querying on every focus. Other events (SIGNED_IN, SIGNED_OUT,
        // USER_UPDATED) propagate the new reference normally.
        setUser((prev) => {
          const next = newSession?.user ?? null;
          if (event === "TOKEN_REFRESHED" && prev && next && prev.id === next.id) {
            return prev;
          }
          return next;
        });
        if (!newSession?.user) {
          setIsPlatformAdmin(false);
          adminCheckDone.current = true;
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  // Phase 2: Admin check — runs after user state changes, outside the auth callback.
  useEffect(() => {
    if (!user) return;

    let cancelled = false;
    adminCheckDone.current = false;

    (async () => {
      const { data, error } = await supabase
        .from("platform_roles")
        .select("role")
        .eq("user_id", user.id)
        .eq("role", "platform_admin")
        .maybeSingle();

      if (cancelled) return;

      const isAdmin = !error && data !== null;
      setIsPlatformAdmin(isAdmin);
      adminCheckDone.current = true;

      if (!isAdmin) {
        await supabase.auth.signOut();
        setUser(null);
        setSession(null);
        setIsPlatformAdmin(false);
      }

      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [user]);

  const signIn = async (email: string, password: string) => {
    const { error: authError } = await supabase.auth.signInWithPassword({ email, password });
    if (authError) return { error: authError.message };
    // onAuthStateChange will fire → sets user → triggers admin check effect
    return { error: null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    setIsPlatformAdmin(false);
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isPlatformAdmin, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
